import { AttributeIdentifiers } from "@lyfegen-platform/graphql-schema/models";
import { distinctBy, getTreatmentCodeByCountry, sortBySelectors } from "@lyfegen-platform/utils";
import { AdverseEventDataPoints } from "../models/flexible/adverse-event-data-points";
import { CADataPoints, PreparationDataPoints, PreparationDistinguishedBrandsDataPoints } from "../models/flexible/ca-data-points";
import { CBDataPoints } from "../models/flexible/cb-data-points";
import { DiscontinuationDataPoints } from "../models/flexible/discontinuation-data-points";
import { DiscontinuationNoteDataPoints } from "../models/flexible/discontinuation-note-data-points";
import { IRDataPoints } from "../models/flexible/ir-data-points";
import { RecordDataPoints } from "../models/flexible/record-data-points";
import { SIDataPoints } from "../models/flexible/subsequent-intervention-data-points";
import { SupportingItemDataPoints } from "../models/flexible/supporting-item-data-points";
import { TypedFlexEntity } from "../models/flexible/typed-flex-entity";
import {
	AgreementScope,
	Brand,
	CatalogueAttribute,
	ConditionLogicalOperator,
	EnrollingConditionSet,
	Maybe,
	Preparation,
	Treatment,
	TreatmentSet,
} from "../models/generated/graphql";
import { anyNamedEntity } from "./any-named-entity";
import { getSectionDataWithAttribute } from "./flex-entity-utils";
import { isEmptyValue } from "./is-empty-value";
import { neo4jDateToDate } from "./neo4j-date-to-date";

export const castToArray = <T>(value: T | Array<T> | null | undefined): Array<T> =>
	Array.isArray(value) ? value : ([value].filter(Boolean) as any);

export const getFirst = <T>(items: T | Array<T>): T | undefined => (Array.isArray(items) ? items[0] : items);

export const getAttributeValuesDeep = <T = any>(data: any, identifier: AttributeIdentifiers): Array<T> =>
	castToArray<any>(getSectionDataWithAttribute<T>(data, identifier)).map((item) => item[identifier]);

export const getAttributeValueDeep = <T = any>(data: any, identifier: AttributeIdentifiers): T | undefined =>
	getFirst<any>(getSectionDataWithAttribute<T>(data, identifier))?.[identifier];

export const areBrandsMatched = (a?: Maybe<Preparation>, b?: Maybe<Brand>) => {
	return a?.BrandName === b?.Name;
};

export const isTreatmentMatchedByBrand = (treatment: Maybe<Treatment> | undefined, brand: Maybe<Brand>): boolean => {
	return treatment?.regulatedBy.BrandName === brand?.Name;
};

export const areTreatmentsMatched = (a: Maybe<Treatment>, b: Maybe<Treatment>): boolean => {
	return !!a && !!b && getTreatmentCodeByCountry(a) === getTreatmentCodeByCountry(b);
};

export const isBrandOrTreatmentMatched = (target: Treatment, brand?: Maybe<Brand>, treatment?: Maybe<Treatment>) => {
	return (
		(!brand && !treatment) ||
		brand?.Name === target.regulatedBy.BrandName ||
		getTreatmentCodeByCountry(treatment) === getTreatmentCodeByCountry(target)
	);
};

export const canBeEvaluated = (date: Maybe<Date> | undefined, executionDate: Date): boolean => {
	return !!date && (neo4jDateToDate(date) as Date) <= executionDate;
};

export const getScopeTreatments = (scope: AgreementScope, treatmentSetFilter?: (treatmentSet: TreatmentSet) => boolean) => {
	const country = scope.enrollingConditionSets[0].agreement!.country;
	return distinctBy(
		scope.treatmentSets
			.filter((treatmentSet) => !treatmentSetFilter || treatmentSetFilter(treatmentSet))
			.flatMap(getTreatmentsByTreatmentSet),
		(treatment) => treatment.Code
	)
		.filter((treatment) => treatment.country.name === country.name)
		.sort(sortBySelectors((treatment) => treatment.Name));
};

export const getScopePreparations = (scope: AgreementScope, treatmentSetFilter?: (treatmentSet: TreatmentSet) => boolean) => {
	const country = scope.enrollingConditionSets[0].agreement!.country;
	return scope.treatmentSets
		.filter((treatmentSet) => !treatmentSetFilter || treatmentSetFilter(treatmentSet))
		.flatMap(getPreparationsByTreatmentSet)
		.filter((preparation) => preparation.regulates.some((treatment) => treatment.country.name === country.name));
};

export const getPreparationsWithDistinguishBrands = (entityData: any) => {
	if (!entityData) {
		return [];
	}

	const preparations = getSectionDataWithAttribute<Array<PreparationDataPoints>>(entityData, AttributeIdentifiers.BrandName) || [];
	return getFlatPreparations(Array.isArray(preparations) ? preparations : [preparations]);
};

export const getPreparations = (entityData: any) => {
	if (!entityData) {
		return [];
	}

	const preparations = getSectionDataWithAttribute<Array<PreparationDataPoints>>(entityData, AttributeIdentifiers.BrandName) || [];
	return Array.isArray(preparations) ? preparations : [preparations];
};

export const getFlatPreparationsIfExist = (entityData: any): Maybe<Array<PreparationDistinguishedBrandsDataPoints>> => {
	if (!entityData) {
		return null;
	}

	const preparations = getSectionDataWithAttribute<Array<PreparationDataPoints>>(entityData, AttributeIdentifiers.BrandName);
	if (!preparations) {
		return null;
	}

	return getFlatPreparations(castToArray(preparations)).filter((section) => section.brandName);
};

export const getPreparationsIfExist = (entityData: any): Maybe<Array<PreparationDataPoints>> => {
	if (!entityData) {
		return null;
	}

	const preparations = getSectionDataWithAttribute<Array<PreparationDataPoints>>(entityData, AttributeIdentifiers.BrandName);
	if (!preparations) {
		return null;
	}

	return castToArray(preparations).filter((section) => section.brandName);
};

export const getCaseEntities = (startingPoint: TypedFlexEntity<any>) => [
	startingPoint,
	...startingPoint.cases.flatMap((cs) => cs.flexEntities),
];

export const getActualWithinRangeStart = (start: Maybe<number> | undefined, end: Maybe<number> | undefined): number => {
	return isEmptyValue(end) ? 0 : start || 0;
};

export const getActualWithinRangeEnd = (start: Maybe<number> | undefined, end: Maybe<number> | undefined): number => {
	return isEmptyValue(end) ? start || 0 : end || 0;
};

export const getAssessmentDate = (entity: TypedFlexEntity<any>) =>
	neo4jDateToDate(
		(entity.data[AttributeIdentifiers.IsLegacyRequestReceiptDate] && entity.data[AttributeIdentifiers.RequestReceiptDate]) ||
			entity.data[AttributeIdentifiers.DiagnosisDate] ||
			entity.data[AttributeIdentifiers.CoverageStartDate] ||
			entity.data[AttributeIdentifiers.TherapyTrialStartDate] ||
			entity.data[AttributeIdentifiers.AssessmentDate] ||
			entity.data[AttributeIdentifiers.ResponseAssessmentDate] ||
			entity.data[AttributeIdentifiers.BenefitAssessmentDate] ||
			entity.data[AttributeIdentifiers.DiscontinuationDate] ||
			entity.data[AttributeIdentifiers.RequestReceiptDate]
	);

export const getTimerameEnd = (timeframe: [Maybe<Date>, Maybe<Date>]) => timeframe[1];

export const isDateMatchedByTimeframe = (timeframe: [Maybe<Date>, Maybe<Date>], date: Date) =>
	(!timeframe[0] || timeframe[0] <= neo4jDateToDate(date)) && (!timeframe[1] || timeframe[1] >= neo4jDateToDate(date));

export const getInitialResponse = (startingPoint: TypedFlexEntity<any>): Maybe<IRDataPoints> | undefined => {
	return returnFirstResult(getCaseEntities(startingPoint), (c) =>
		getSectionDataWithAttribute<IRDataPoints>(c.data, AttributeIdentifiers.ResponseAssessmentDate)
	);
};

export const getDiscontinuationNote = (startingPoint: TypedFlexEntity<any>): Maybe<DiscontinuationNoteDataPoints> | undefined => {
	return returnFirstResult(getCaseEntities(startingPoint), (container) =>
		getSectionDataWithAttribute<DiscontinuationNoteDataPoints>(container.data, AttributeIdentifiers.DiscontinuationDate)
	);
};

export const getDiscontinuationDataPoints = (startingPoint: TypedFlexEntity<any>): Maybe<DiscontinuationDataPoints> | undefined => {
	return returnFirstResult(getCaseEntities(startingPoint), (container) =>
		getSectionDataWithAttribute<DiscontinuationNoteDataPoints>(container.data, AttributeIdentifiers.PatientEnrolledInTreatment)
	);
};

export const getContinuousBenefits = (startingPoint: TypedFlexEntity<any>): Array<CBDataPoints> => {
	return getCaseEntities(startingPoint)
		.map((container) => getSectionDataWithAttribute<CBDataPoints>(container.data, AttributeIdentifiers.BenefitAssessmentDate))
		.filter(Boolean);
};

export const getSupportingItems = (entity: TypedFlexEntity<any>): Array<SupportingItemDataPoints> => {
	return (
		getSectionDataWithAttribute<Array<SupportingItemDataPoints>>(entity.data, AttributeIdentifiers.SupportingItemModel).filter(
			Boolean
		) || []
	);
};

export const returnFirstResult = <T, R>(items: Array<T>, mapper: (e: T) => R): R | undefined => {
	for (const item of items) {
		const itemResult = mapper(item);
		if (!!itemResult) {
			return itemResult;
		}
	}
	return undefined;
};

export const getRecordTreatments = (record: TypedFlexEntity<RecordDataPoints>): Array<Treatment> =>
	(record.data.productName?.length ? record.data.productName : [record.data.packSize]).filter(Boolean) as Array<Treatment>;

export const getRecordDate = (record: TypedFlexEntity<RecordDataPoints>) =>
	neo4jDateToDate(record.data.administrationDate) || neo4jDateToDate(record.data.prescriptionDate);

export const getSubsequentInterventions = (startingPoint: TypedFlexEntity<any>): Maybe<Array<SIDataPoints>> | undefined => {
	return [startingPoint, ...getCaseEntities(startingPoint)].map((c) =>
		getSectionDataWithAttribute<SIDataPoints>(c.data, AttributeIdentifiers.ResponseAssessmentDate)
	);
};

export const getAdverseEventsDataPoints = (startingPoint: TypedFlexEntity<any>) => {
	return (
		[startingPoint, ...getCaseEntities(startingPoint)]
			.flatMap((e) => getSectionDataWithAttribute<Array<AdverseEventDataPoints>>(e, AttributeIdentifiers.AdverseEvent))
			.filter(Boolean) || []
	);
};

function getFlatPreparations(data: Array<PreparationDataPoints>): Array<PreparationDistinguishedBrandsDataPoints> {
	return data.flatMap((item) =>
		typeof item.brandName === 'string'? {
			brandName: item.brandName,
			genericName: item.genericName,
		}: item.brandName.map((brand) => ({
			brandName: brand,
			genericName: item.genericName,
		}))
	);
}

function isTreatmentSetMatched(preparationContainer: any, treatmentSet: TreatmentSet): boolean {
	const isGenericMatched = treatmentSet.generic.Name === preparationContainer[AttributeIdentifiers.GenericName].Name;
	const preparationBrandNames = preparationContainer[AttributeIdentifiers.BrandName]?.map((brand: any) => brand.Name);
	const isBrandMatched = treatmentSet.preparations.some((preparation) => preparationBrandNames.includes(preparation.BrandName));
	return (
		isBrandMatched ||
		(isGenericMatched && treatmentSet.preparations.length === 0) ||
		(isGenericMatched && preparationBrandNames.includes(anyNamedEntity.Name))
	);
}

function areTreatmentSetsMatched(startingPointData: any, agreementScope: AgreementScope): boolean {
	const preparations = getPreparations(startingPointData);
	if (
		agreementScope.logicalOperator === ConditionLogicalOperator.And
			? preparations.length !== agreementScope.treatmentSets.length
			: preparations.length > 1
	) {
		return false;
	}

	return agreementScope.logicalOperator === ConditionLogicalOperator.And
		? agreementScope.treatmentSets.every((treatmentSet) =>
				preparations.some((preparation) => isTreatmentSetMatched(preparation, treatmentSet))
		  )
		: agreementScope.treatmentSets.some((treatmentSet) =>
				preparations.some((preparation) => isTreatmentSetMatched(preparation, treatmentSet))
		  );
}

function matchDiseaseByCode(code: Maybe<string>, category: Maybe<string>) {
	if (!code || !category) {
		throw new Error("isDiseaseCodeMatched: Argument null exception");
	}

	const codeComponents = code.split(".");
	const categoryComponents = category.split(".");
	return categoryComponents.every((c, i) => (i === 0 ? c === codeComponents[i] : codeComponents[i]?.startsWith(c)));
}

function isDiseaseMatched(startingPointData: any, agreementScope: AgreementScope, conditionSet: EnrollingConditionSet) {
	if (conditionSet.isDiseaseNotEnrollingCondition) {
		return true;
	}

	return (
		!startingPointData[AttributeIdentifiers.Disease] ||
		!agreementScope.diseases.length ||
		agreementScope.diseases.some((d) => matchDiseaseByCode(startingPointData[AttributeIdentifiers.Disease]?.Icd10Code, d?.Icd10Code))
	);
}

function isIndicationMatched(startingPointData: any, agreementScope: AgreementScope, conditionSet: EnrollingConditionSet) {
	if (conditionSet.isIndicationNotEnrollingCondition) {
		return true;
	}

	return (
		!startingPointData[AttributeIdentifiers.Indication] ||
		!agreementScope.indications.length ||
		agreementScope.indications.some(
			(i) =>
				i.code ===
				(typeof startingPointData[AttributeIdentifiers.Indication] === "string"
					? startingPointData[AttributeIdentifiers.Indication]
					: startingPointData[AttributeIdentifiers.Indication].code)
		)
	);
}

interface IsConditionSetMatchedArgs {
	values: CADataPoints;
	agreementScope: AgreementScope;
	conditionSet: EnrollingConditionSet;
	ignoredAttributes: Array<string>;
}

export function isConditionSetMatched({ values, agreementScope, conditionSet, ignoredAttributes }: IsConditionSetMatchedArgs) {
	return (
		(!!ignoredAttributes?.includes(AttributeIdentifiers.Disease) || isDiseaseMatched(values, agreementScope, conditionSet)) &&
		(!!ignoredAttributes?.includes(AttributeIdentifiers.Indication) || isIndicationMatched(values, agreementScope, conditionSet)) &&
		areTreatmentSetsMatched(values, agreementScope)
	);
}

export const getTreatmentsByTreatmentSet = (treatmentSet: TreatmentSet): Array<Treatment> => {
	if (treatmentSet.treatments.length) {
		return treatmentSet.treatments;
	}

	if (treatmentSet.preparations.length) {
		return treatmentSet.preparations.flatMap((preparation) => preparation.regulates);
	}

	return treatmentSet.generic.preparations.flatMap((preparation) => preparation.regulates);
};

export const getPreparationsByTreatmentSet = (treatmentSet: TreatmentSet): Array<Preparation> =>
	treatmentSet.preparations.length ? treatmentSet.preparations : treatmentSet.generic.preparations;

export const combineIgnoredAndMissingAttributes = (
	ignoredAttributes: Array<string>,
	includedCatalogueAttributes: Array<CatalogueAttribute>
): Array<AttributeIdentifiers> => {
	const ignoredAttributeIdentifiers = ignoredAttributes.map(
		(ignoredAttribute) => includedCatalogueAttributes.find((includedAttribute) => includedAttribute.id === ignoredAttribute)!.identifier
	);

	const isIndicationIncluded = !!includedCatalogueAttributes.find(
		(includedAttribute) => includedAttribute.identifier === AttributeIdentifiers.Indication
	);
	const isDiseasesIncluded = !!includedCatalogueAttributes.find(
		(includedAttribute) => includedAttribute.identifier === AttributeIdentifiers.Disease
	);

	!isDiseasesIncluded && ignoredAttributeIdentifiers.push(AttributeIdentifiers.Disease);
	!isIndicationIncluded && ignoredAttributeIdentifiers.push(AttributeIdentifiers.Indication);

	return ignoredAttributeIdentifiers as Array<AttributeIdentifiers>;
};
